import trim from "lodash/trim";
import { Button } from "PFComponents/button";
import { Inline } from "PFComponents/containers";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useKPIReviewCreate } from "PFCore/hooks/queries/kpi/use_kpi_review_create";
import { Id, PermissionRule } from "PFCore/types";
import { KPIStatus as KPIStatusEnum } from "PFCore/types/kpi";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { KPIEventTileBase } from "./kpi_event_tile_base";

type KPIActionTileProps = {
  KPIId: Id;
  engagementId: Id;
  KPIName: string;
  KPIStatus?: KPIStatusEnum;
};

export const KPIActionTile = ({ KPIId, KPIName, KPIStatus, engagementId }: KPIActionTileProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "KPIsReviewSidepanel" });
  const isApprover = useIsCurrentUserPermittedTo(PermissionRule.KPIsApprover);
  const isRequester = useIsCurrentUserPermittedTo(PermissionRule.KPIsRequestApproval);
  const { mutate: createKPIReview, isPending } = useKPIReviewCreate({
    onSuccess: () => setComment("")
  });
  const [comment, setComment] = useState("");

  const canSeeApproveButton = isApprover && KPIStatus === KPIStatusEnum.ApprovalRequested;
  const canSeeRejectButton = isApprover && KPIStatus === KPIStatusEnum.ApprovalRequested;
  const canSeeRequestApprovalButton =
    isRequester &&
    [KPIStatusEnum.ApprovalRequired, KPIStatusEnum.ApprovalRejected].includes(KPIStatus as KPIStatusEnum);

  if (!canSeeApproveButton && !canSeeRejectButton && !canSeeRequestApprovalButton) {
    return null;
  }

  const trimmedComment = trim(comment);
  const commonRequestPayload = {
    indicatorId: KPIId,
    engagementId,
    comment: trimmedComment
  };

  return (
    <KPIEventTileBase loading={isPending}>
      <InputFieldSet
        inputType="textarea"
        label={
          isApprover ? t("reasonToApproveOrRejectKPI", { KPIName }) : t("reasonToApproveKPI", { KPIName })
        }
        value={comment}
        onChange={setComment}
        disabled={isPending}
        maxLength={250}
        minVisibleMaxLengthFactor={2}
        required
      />
      <Inline justifyContent="end">
        {canSeeRejectButton && (
          <Button
            kind="danger"
            onClick={() =>
              createKPIReview({ ...commonRequestPayload, status: KPIStatusEnum.ApprovalRejected })
            }
            disabled={!trimmedComment || isPending}
          >
            {t("reject")}
          </Button>
        )}
        {canSeeApproveButton && (
          <Button
            onClick={() =>
              createKPIReview({ ...commonRequestPayload, status: KPIStatusEnum.ApprovalGranted })
            }
            disabled={!trimmedComment || isPending}
          >
            {t("approve")}
          </Button>
        )}
        {canSeeRequestApprovalButton && (
          <Button
            onClick={() =>
              createKPIReview({ ...commonRequestPayload, status: KPIStatusEnum.ApprovalRequested })
            }
            disabled={!trimmedComment || isPending}
          >
            {t("requestApproval")}
          </Button>
        )}
      </Inline>
    </KPIEventTileBase>
  );
};
